import algoliasearch, { SearchClient, SearchIndex } from "algoliasearch";
import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { normalizePhoneSearchQuery } from "@/lib/phoneHelpers";
import { appendLowRankHits } from "./appendLowRankHits";
import { collectFilteredHits } from "./collectFilteredHits";

const appId = import.meta.env.VITE_ALGOLIA_APP_ID;
const apiKey = import.meta.env.VITE_ALGOLIA_API_KEY;
const SCROLL_VISIBILITY_PADDING = 8;
const stickySearchHeaderSelector = "[data-algolia-sticky-search-header='true']";

function getScrollableAncestor(element: HTMLElement): HTMLElement | null {
  let current: HTMLElement | null = element.parentElement;

  while (current) {
    const { overflowY } = window.getComputedStyle(current);
    const canScroll = (overflowY === "auto" || overflowY === "scroll") && current.scrollHeight > current.clientHeight;

    if (canScroll) return current;

    current = current.parentElement;
  }

  return null;
}

function scrollHighlightedItemIntoView(highlightedItem: HTMLElement, fallbackContainer: HTMLElement) {
  const scrollContainer = getScrollableAncestor(highlightedItem) || fallbackContainer;
  const itemRect = highlightedItem.getBoundingClientRect();
  const containerRect = scrollContainer.getBoundingClientRect();
  const stickyHeader = scrollContainer.querySelector<HTMLElement>(stickySearchHeaderSelector);
  const stickyHeaderRect = stickyHeader?.getBoundingClientRect();
  const visibleTop =
    Math.max(containerRect.top, stickyHeaderRect?.bottom || containerRect.top) + SCROLL_VISIBILITY_PADDING;
  const visibleBottom = containerRect.bottom - SCROLL_VISIBILITY_PADDING;

  if (itemRect.top < visibleTop) {
    scrollContainer.scrollTo({
      top: scrollContainer.scrollTop - (visibleTop - itemRect.top)
    });
  } else if (itemRect.bottom > visibleBottom) {
    scrollContainer.scrollTo({
      top: scrollContainer.scrollTop + (itemRect.bottom - visibleBottom)
    });
  }
}

export type AdditionalAlgoliaIndex<T> = {
  indexName: string;
  /** Mixed-list cap for secondary hits. Empty primary lists fill leftover slots up to `hitsPerPage`. */
  maxHits: number;
  searchParams?: Record<string, unknown>;
  mapHits: (hits: Array<Record<string, unknown>>) => T[];
};

export interface UseAlgoliaSearchOptions<T> {
  indexName: string;
  hitsPerPage?: number;
  initialUiState?: Record<string, any>;
  displayKey?: string;
  getDisplayValue?: (hit: T) => string;
  isExactMatch?: (hit: T, query: string) => boolean;
  filterHits?: (hits: T[], query: string) => T[];
  additionalIndex?: AdditionalAlgoliaIndex<T>;
  onSelect?: (hit: T) => void;
  onClear?: () => void;
  onSubmit?: (query: string) => void;
  defaultValue?: string;
}

export interface UseAlgoliaSearchReturn<T> {
  query: string;
  displayValue: string | undefined;
  results: T[];
  isLoading: boolean;
  showResults: boolean;
  highlightedIndex: number;
  setHighlightedIndex: React.Dispatch<React.SetStateAction<number>>;
  handleQueryChange: (query: string) => void;
  handleClear: () => void;
  handleItemSelected: (hit: T) => void;
  setShowResults: (show: boolean) => void;
  setIsFocused: (focused: boolean) => void;
  setDisplayValue: (value: string | undefined) => void;
  focusInput: () => void;
  itemRefs: React.MutableRefObject<Array<HTMLDivElement | null>>;
  resultsContainerRef: React.RefObject<HTMLDivElement>;
  searchBoxRef: React.RefObject<HTMLDivElement>;
}

export function useAlgoliaSearch<T extends Record<string, any>>({
  indexName,
  hitsPerPage = 7,
  initialUiState,
  displayKey = "title",
  getDisplayValue,
  isExactMatch,
  filterHits,
  additionalIndex,
  onSelect,
  onClear,
  onSubmit,
  defaultValue
}: UseAlgoliaSearchOptions<T>): UseAlgoliaSearchReturn<T> {
  const [showResults, setShowResults] = useState(false);
  const [selectedValue, setSelectedValue] = useState<string | undefined>(defaultValue);
  const [currentQuery, setCurrentQuery] = useState("");
  const [searchResults, setSearchResults] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);

  const searchBoxRef = useRef<HTMLDivElement>(null!);
  const resultsContainerRef = useRef<HTMLDivElement>(null!);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const requestIdRef = useRef(0);
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
  const isFocusedRef = useRef(false);

  const highlightedIndexRef = useRef(highlightedIndex);
  highlightedIndexRef.current = highlightedIndex;
  const isExactMatchRef = useRef(isExactMatch);
  isExactMatchRef.current = isExactMatch;
  const filterHitsRef = useRef(filterHits);
  filterHitsRef.current = filterHits;
  const additionalIndexRef = useRef(additionalIndex);
  additionalIndexRef.current = additionalIndex;
  const searchResultsRef = useRef(searchResults);
  searchResultsRef.current = searchResults;
  const showResultsRef = useRef(showResults);
  showResultsRef.current = showResults;
  const currentQueryRef = useRef(currentQuery);
  currentQueryRef.current = currentQuery;
  const onSubmitRef = useRef(onSubmit);
  onSubmitRef.current = onSubmit;
  const handleItemSelectedRef = useRef<(hit: T) => void>(() => {});
  const performSearchRef = useRef<(query: string) => void>(() => {});

  const searchClient: SearchClient = useMemo(() => {
    return algoliasearch(appId, apiKey);
  }, []);

  const index: SearchIndex = useMemo(() => {
    return searchClient.initIndex(indexName);
  }, [searchClient, indexName]);

  const secondaryIndex: SearchIndex | null = useMemo(() => {
    if (!additionalIndex?.indexName) {
      return null;
    }
    return searchClient.initIndex(additionalIndex.indexName);
  }, [searchClient, additionalIndex?.indexName]);

  const searchConfig = useMemo(() => {
    if (!initialUiState || !initialUiState[indexName]) {
      return {};
    }
    return initialUiState[indexName].configure || {};
  }, [initialUiState, indexName]);

  useEffect(() => {
    if (defaultValue !== undefined) {
      setSelectedValue(defaultValue);
    }
  }, [defaultValue]);

  const performSearch = useCallback(
    async (query: string) => {
      const requestId = ++requestIdRef.current;

      if (query.trim() === "") {
        setSearchResults([]);
        setShowResults(false);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const normalizedQuery = normalizePhoneSearchQuery(query);
        const extra = additionalIndexRef.current;
        const primaryPromise = filterHitsRef.current
          ? collectFilteredHits<T>(index, normalizedQuery, {
              searchParams: searchConfig,
              filterHits: filterHitsRef.current,
              limit: hitsPerPage
            }).then(({ hits }) => hits)
          : index
              .search(normalizedQuery, { hitsPerPage, ...searchConfig })
              .then((results) => results.hits as unknown as T[]);

        const secondaryPromise =
          secondaryIndex && extra
            ? secondaryIndex
                .search(normalizedQuery, {
                  hitsPerPage,
                  ...extra.searchParams
                })
                .then((secondaryResults) =>
                  extra.mapHits(secondaryResults.hits as unknown as Array<Record<string, unknown>>)
                )
                .catch((secondaryError) => {
                  console.error("Algolia additional index search error:", secondaryError);
                  return [] as T[];
                })
            : Promise.resolve([] as T[]);

        const [primaryHits, secondaryHits] = await Promise.all([primaryPromise, secondaryPromise]);
        if (requestId !== requestIdRef.current) return;
        setSearchResults(
          extra ? appendLowRankHits(primaryHits, secondaryHits, hitsPerPage, extra.maxHits) : primaryHits
        );
        setShowResults(true);
      } catch (error) {
        if (requestId !== requestIdRef.current) return;
        console.error("Algolia search error:", error);
        setSearchResults([]);
      } finally {
        if (requestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    },
    [index, secondaryIndex, hitsPerPage, searchConfig]
  );

  const handleQueryChange = useCallback(
    (query: string) => {
      setCurrentQuery(query);
      requestIdRef.current += 1;

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = null;
      }

      if (query.trim() === "") {
        setSearchResults([]);
        setShowResults(false);
        setIsLoading(false);
        return;
      }

      searchTimeoutRef.current = setTimeout(() => {
        performSearch(query);
      }, 300);
    },
    [performSearch]
  );

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!showResults) {
      setHighlightedIndex(-1);
    } else if (
      isExactMatchRef.current &&
      searchResults.length > 0 &&
      isExactMatchRef.current(searchResults[0], currentQuery)
    ) {
      setHighlightedIndex(0);
    } else if (searchResults.length === 1) {
      setHighlightedIndex(0);
    } else {
      setHighlightedIndex(-1);
    }
  }, [showResults, searchResults, currentQuery]);

  useEffect(() => {
    if (!resultsContainerRef.current) return;
    if (highlightedIndex < 0) return;
    const highlightedItem = itemRefs.current[highlightedIndex];
    if (!highlightedItem) return;

    scrollHighlightedItemIntoView(highlightedItem, resultsContainerRef.current);
  }, [highlightedIndex]);

  const focusInput = useCallback(() => {
    requestAnimationFrame(() => {
      const textField =
        searchBoxRef.current?.querySelector<HTMLElement>("md-outlined-text-field") ||
        searchBoxRef.current?.querySelector<HTMLElement>("md-filled-text-field");
      textField?.focus();
    });
  }, []);

  const handleClear = useCallback(() => {
    requestIdRef.current += 1;
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    setSelectedValue(undefined);
    setCurrentQuery("");
    setSearchResults([]);
    setShowResults(false);
    setIsLoading(false);
    if (onClear) {
      onClear();
    }
    focusInput();
  }, [onClear, focusInput]);

  const handleItemSelected = useCallback(
    (hit: T) => {
      setShowResults(false);

      let displayValue = "";
      if (getDisplayValue) {
        displayValue = getDisplayValue(hit);
      } else if (hit && displayKey && hit[displayKey]) {
        displayValue = hit[displayKey];
      } else if (hit && Object.keys(hit).length > 0) {
        const potentialKeys = ["name", "title", "label", "id", "objectID"];
        for (const key of potentialKeys) {
          if (hit[key] && typeof hit[key] === "string") {
            displayValue = hit[key];
            break;
          }
        }
      }

      setSelectedValue(displayValue);
      setCurrentQuery("");
      setSearchResults([]);

      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }

      if (onSelect) {
        onSelect(hit);
      }
    },
    [getDisplayValue, displayKey, onSelect]
  );

  handleItemSelectedRef.current = handleItemSelected;
  performSearchRef.current = performSearch;

  useEffect(() => {
    const handler = (e: globalThis.KeyboardEvent) => {
      if (!isFocusedRef.current) return;

      const target = e.target as HTMLElement;
      if (target?.closest("[aria-label='Clear search query']")) return;

      const results = searchResultsRef.current;
      const hasResults = results.length > 0;
      const idx = highlightedIndexRef.current;

      if (e.key === "ArrowDown") {
        if (!showResultsRef.current && currentQueryRef.current.trim() !== "" && hasResults) {
          setShowResults(true);
        }
        if (hasResults) {
          e.preventDefault();
          setHighlightedIndex((prev) => (prev + 1) % results.length);
        }
        return;
      }

      if (e.key === "ArrowUp") {
        if (hasResults) {
          e.preventDefault();
          setHighlightedIndex((prev) => (prev - 1 + results.length) % results.length);
        }
        return;
      }

      if (e.key === "Enter") {
        if (showResultsRef.current && idx >= 0 && idx < results.length) {
          e.preventDefault();
          e.stopImmediatePropagation();
          handleItemSelectedRef.current(results[idx]);
          return;
        }
        if (currentQueryRef.current.trim() !== "") {
          if (onSubmitRef.current) {
            e.preventDefault();
            e.stopImmediatePropagation();
            onSubmitRef.current(currentQueryRef.current);
          } else {
            performSearchRef.current(currentQueryRef.current);
          }
        }
      }
    };

    window.addEventListener("keydown", handler, true);
    return () => window.removeEventListener("keydown", handler, true);
  }, []);

  return {
    query: currentQuery,
    displayValue: selectedValue,
    results: searchResults,
    isLoading,
    showResults,
    highlightedIndex,
    setHighlightedIndex,
    handleQueryChange,
    handleClear,
    handleItemSelected,
    setShowResults,
    setIsFocused: (focused: boolean) => {
      isFocusedRef.current = focused;
    },
    setDisplayValue: setSelectedValue,
    focusInput,
    itemRefs,
    resultsContainerRef,
    searchBoxRef
  };
}
